import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: BASE_URL,
});

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const verifyToken = (token: string) =>
  api.post('/api/auth/verify', { token });

// ---------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------

export const getSources = () => api.get('/api/sources');
export const getSource = (name: string) => api.get(`/api/sources/${name}`);
export const createSource = (data: any) => api.post('/api/sources', data);
export const updateSource = (name: string, data: any) => api.put(`/api/sources/${name}`, data);
export const renameSource = (name: string, new_name: string) =>
  api.post(`/api/sources/${name}/rename`, { new_name });
export const deleteSource = (name: string) => api.delete(`/api/sources/${name}`);
export const testSource = (name: string) => api.post(`/api/sources/${name}/test`);
export const refreshSourceCache = (name: string) =>
  api.post(`/api/sources/${name}/cache/refresh`);
export const getSourceSchemas = (name: string) => api.get(`/api/sources/${name}/schemas`);
export const getSourceTables = (name: string, schema: string) =>
  api.get(`/api/sources/${name}/schemas/${schema}/tables`);
export const getSourceViews = (name: string, schema: string) =>
  api.get(`/api/sources/${name}/schemas/${schema}/views`);
export const getTableCount = (name: string, schema: string, table: string) =>
  api.get(`/api/sources/${name}/schemas/${schema}/tables/${table}/count`);
export const getTableColumns = (name: string, schema: string, table: string) =>
  api.get(`/api/sources/${name}/schemas/${schema}/tables/${table}/columns`);

// Drive spreadsheet sources (ADR-46). The tab goes in the query string, not
// the path: real tab names contain spaces and punctuation.
export interface DriveTab {
  name: string;
  hidden: boolean;
}
export interface DrivePreviewColumn {
  name: string;            // normalised column name
  header: string | null;   // header text as it appears in the sheet
  letter: string;          // spreadsheet column letter
}
export interface DrivePreview {
  tab: string;
  header_row: number;
  columns: DrivePreviewColumn[];
  rows: (string | null)[][];
  row_numbers: number[];
  total_rows: number;
}
export const getDriveServiceAccount = () =>
  api.get<{ email: string | null }>('/api/sources/_drive/service-account');
export const getDriveTabs = (name: string) =>
  api.get<{ file_name: string | null; tabs: DriveTab[] }>(`/api/sources/${name}/tabs`);
export const previewDriveTab = (name: string, tab: string, header_row: number) =>
  api.get<DrivePreview>(`/api/sources/${name}/preview`, { params: { tab, header_row } });

// Drive spreadsheet changes awaiting review (ADR-46). Each stays open until a
// user resolves it here; a later successful run doesn't close it.
export type SheetDriftAction =
  | 'set_header_row'
  | 'use_tab'
  | 'remove_tab'
  | 'accept_current'
  | 'accept_rename'
  | 'retire'
  | 'acknowledge';
export interface SheetDriftItem {
  id: string;
  pipeline_name: string;
  tab: string;                 // "*" = the whole file
  kind: string;
  column: string | null;
  message: string | null;
  detail: Record<string, any>;
  occurrences: number;
  opened_at: string;
  last_seen_at: string;
  last_loaded_at: string | null;
  blocking: boolean;
  actions: SheetDriftAction[];
}
export const getSheetDrift = () => api.get<SheetDriftItem[]>('/api/sheet-drift');

// Source tags, Private flag and access lists (ADR-47). Admin only — the
// server enforces it (require_admin); hiding Settings from members is cosmetic.
export interface SourcePermissions {
  run: boolean;
  view: boolean;
  edit: boolean;
  delete: boolean;
}
export interface AccessTag {
  tag_id: string;
  label: string;
  sources: string[];
  // ADR-57: full access to sources a team-limited member creates under this tag.
  default_owners: string[];
}
export interface AccessSourceRow {
  name: string;
  db_type: string;
  is_private: boolean;
  tags: string[];
  grant_count: number;
  pipelines: string[];
}
export interface AccessGrantRow {
  user_email: string;
  permissions: SourcePermissions;
  granted_at: string | null;
  granted_by: string | null;
}
export interface AccessHistoryRow {
  changed_at: string;
  actor: string;
  action: string;
  source_name: string | null;
  tag_id: string | null;
  user_email: string | null;
  detail: any;
}
const enc = encodeURIComponent;
export const getAccessTags = () => api.get<AccessTag[]>('/api/access/tags');
export const createAccessTag = (label: string) => api.post('/api/access/tags', { label });
export const renameAccessTag = (tagId: string, label: string) =>
  api.put(`/api/access/tags/${enc(tagId)}`, { label });
export const deleteAccessTag = (tagId: string) => api.delete(`/api/access/tags/${enc(tagId)}`);
export const setAccessTagOwners = (tagId: string, owners: string[]) =>
  api.put(`/api/access/tags/${enc(tagId)}/owners`, { owners });
export const getAccessSources = () => api.get<AccessSourceRow[]>('/api/access/sources');
export const setSourceTags = (name: string, tag_ids: string[]) =>
  api.put(`/api/access/sources/${enc(name)}/tags`, { tag_ids });
export const setSourcePrivate = (name: string, isPrivate: boolean) =>
  api.put(`/api/access/sources/${enc(name)}/private`, { private: isPrivate });
export const getSourceGrants = (name: string) =>
  api.get<AccessGrantRow[]>(`/api/access/sources/${enc(name)}/grants`);
export const setSourceGrant = (name: string, email: string, permissions: SourcePermissions) =>
  api.put(`/api/access/sources/${enc(name)}/grants/${enc(email)}`, permissions);
export const revokeSourceGrant = (name: string, email: string) =>
  api.delete(`/api/access/sources/${enc(name)}/grants/${enc(email)}`);
export const getAccessHistory = (source?: string) =>
  api.get<AccessHistoryRow[]>('/api/access/history', { params: source ? { source } : {} });
export const resolveSheetDrift = (id: string, action: SheetDriftAction, value?: number | string) =>
  api.post(`/api/sheet-drift/${id}/resolve`, { action, value });

// ---------------------------------------------------------------------------
// Destinations
// ---------------------------------------------------------------------------

export const getDestinations = () => api.get('/api/destinations');
export const createDestination = (data: any) => api.post('/api/destinations', data);
export const updateDestination = (name: string, data: any) => api.put(`/api/destinations/${name}`, data);
export const deleteDestination = (name: string) => api.delete(`/api/destinations/${name}`);
export const testDestination = (name: string) => api.post(`/api/destinations/${name}/test`);

// ---------------------------------------------------------------------------
// Pipelines
// ---------------------------------------------------------------------------

export const getPipelines = () => api.get('/api/pipelines');
export const getPipeline = (name: string) => api.get(`/api/pipelines/${name}`);
export const createPipeline = (data: any) => api.post('/api/pipelines', data);
export const updatePipeline = (name: string, data: any) => api.put(`/api/pipelines/${name}`, data);
export const deletePipeline = (name: string) => api.delete(`/api/pipelines/${name}`);
export const togglePipeline = (name: string, enabled: boolean) =>
  api.patch(`/api/pipelines/${name}/toggle`, { enabled });
export const runPipeline = (name: string) => api.post(`/api/pipelines/${name}/run`);

// ADR-54: turn a table off in `name` and on in a grouped pipeline, in one go.
export interface TableMoveResult {
  moved: string;
  from_pipeline: string;
  to_pipeline: string;
  previous_destination: string;
  destination: string;
  watermark: 'carried' | 'reset' | 'none';
}
// ADR-56 (DATA-114): ask an admin for a schedule outside the policy.
export type ScheduleRequestStatus =
  | 'pending' | 'approved' | 'denied' | 'countered' | 'accepted' | 'rejected' | 'withdrawn' | 'superseded';
export interface ScheduleRequest {
  id: string;
  pipeline_name: string;
  requested_by: string;
  requested_schedule: string;
  reason: string;
  justification: string;
  status: ScheduleRequestStatus;
  previous_schedule?: string | null;
  fallback_schedule?: string | null;
  admin_schedule?: string | null;
  admin_notes?: string | null;
  user_notes?: string | null;
  applied_schedule?: string | null;
  decided_by?: string | null;
  decided_at?: string | null;
  responded_at?: string | null;
  created_at: string;
  updated_at: string;
}
export const createScheduleRequest = (body: {
  pipeline_name: string; requested_schedule: string; reason: string; justification: string;
  previous_schedule?: string | null;
}) => api.post<ScheduleRequest>('/api/schedule-requests', body);
export const getScheduleRequests = () => api.get<ScheduleRequest[]>('/api/schedule-requests');
export const approveScheduleRequest = (id: string, notes?: string) =>
  api.post<ScheduleRequest>(`/api/schedule-requests/${id}/approve`, { notes: notes || null });
export const denyScheduleRequest = (id: string, notes: string) =>
  api.post<ScheduleRequest>(`/api/schedule-requests/${id}/deny`, { notes });
export const counterScheduleRequest = (id: string, schedule: string, notes: string) =>
  api.post<ScheduleRequest>(`/api/schedule-requests/${id}/counter`, { schedule, notes });
export const acceptScheduleRequest = (id: string) => api.post<ScheduleRequest>(`/api/schedule-requests/${id}/accept`);
export const rejectScheduleRequest = (id: string, notes?: string) =>
  api.post<ScheduleRequest>(`/api/schedule-requests/${id}/reject`, { notes: notes || null });
export const withdrawScheduleRequest = (id: string) => api.post<ScheduleRequest>(`/api/schedule-requests/${id}/withdraw`);

export const moveTable = (name: string, destination_table: string, target_pipeline: string) =>
  api.post<TableMoveResult>(`/api/pipelines/${name}/tables/move`, { destination_table, target_pipeline });

// ADR-51: the quietest pinned-minute versions of a cron, given current load.
// `pipeline` is the one being edited, so it isn't counted against itself.
export interface ScheduleScore {
  schedule: string;
  peak_concurrent: number;
  avg_concurrent: number;
  starts_alongside: number;
}
export interface ScheduleSuggestions {
  current: ScheduleScore;
  suggestions: ScheduleScore[];
  duration_seconds: number;
  duration_known: boolean;
  pipelines_considered: number;
  // ADR-56: whether this user may save it, and the fastest schedule the
  // policy allows (offered instead, and run while an escalation is open).
  policy?: { compliant: boolean; reasons: string[]; analysed: boolean } | null;
  needs_approval?: boolean;
  fallback?: string | null;
}
export const getScheduleSuggestions = (schedule: string, pipeline?: string | null) =>
  api.get<ScheduleSuggestions>('/api/pipelines/schedule/suggestions', {
    params: pipeline ? { schedule, pipeline } : { schedule },
  });

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

// `day` (YYYY-MM-DD, UTC) keeps runs that started that day (DATA-368).
export const getRuns = (limit = 50, pipeline_name?: string, day?: string) =>
  api.get('/api/runs', { params: { limit, pipeline_name, day } });
export const getRun = (run_id: string) => api.get(`/api/runs/${run_id}`);

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export const getDashboardStats = () => api.get('/api/dashboard');

// ---------------------------------------------------------------------------
// Users (admin only)
// ---------------------------------------------------------------------------

export const getUsers = () => api.get('/api/users');
// team_tag (ADR-57): limit a member to one team's sources; null = not limited.
export const createUser = (data: {
  email: string;
  name: string;
  is_admin: boolean;
  team_tag?: string | null;
}) => api.post('/api/users', data);
export const updateUser = (
  email: string,
  data: { name: string; is_admin: boolean; team_tag?: string | null },
) => api.put(`/api/users/${encodeURIComponent(email)}`, data);
export const deleteUser = (email: string) =>
  api.delete(`/api/users/${encodeURIComponent(email)}`);
// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export const getNotifications = (include_resolved = false, limit = 100) =>
  api.get('/api/notifications', { params: { include_resolved, limit } });
export const markNotificationRead = (id: string) =>
  api.post(`/api/notifications/${encodeURIComponent(id)}/read`);
export const markAllNotificationsRead = () =>
  api.post('/api/notifications/read-all');

// ---------------------------------------------------------------------------
// Insights (admin only — the server enforces this with require_admin)
// ---------------------------------------------------------------------------

export const getInsights = (hours = 24) =>
  api.get('/api/insights', { params: { hours } });

// ---------------------------------------------------------------------------
// Settings (admin only — the server enforces this with require_admin)
// ---------------------------------------------------------------------------

export const getSchedulePolicy = () => api.get('/api/settings/schedule-policy');
export const updateSchedulePolicy = (data: {
  min_interval_minutes: number;
  working_hours_start: number;
  working_hours_end: number;
  working_days: number[];
}) => api.put('/api/settings/schedule-policy', data);
export const previewSchedulePolicy = (data: {
  min_interval_minutes: number;
  working_hours_start: number;
  working_hours_end: number;
  working_days: number[];
}) => api.post('/api/settings/schedule-policy/preview', data);

// ---------------------------------------------------------------------------
// Reverse-ETL exports (admin only — the server enforces this with require_admin)
//
// Preview writes nothing; run() writes to a production source. Both return
// their result synchronously rather than a run id to poll, unlike runPipeline.
// ---------------------------------------------------------------------------

export interface ExportPayload {
  name: string;
  sql_query: string;
  target_source_name: string;
  target_table: string;
  key_column: string;
  target_schema?: string;
  description?: string | null;
  column_map?: Record<string, string>;
  drop_columns?: string[];
  enabled?: boolean;
}

export const getExports = () => api.get('/api/exports');
export const getExport = (name: string) =>
  api.get(`/api/exports/${encodeURIComponent(name)}`);
export const createExport = (data: ExportPayload) => api.post('/api/exports', data);
export const updateExport = (name: string, data: ExportPayload) =>
  api.put(`/api/exports/${encodeURIComponent(name)}`, data);
export const deleteExport = (name: string) =>
  api.delete(`/api/exports/${encodeURIComponent(name)}`);
export const previewExport = (name: string) =>
  api.post(`/api/exports/${encodeURIComponent(name)}/preview`);
export const runExport = (name: string) =>
  api.post(`/api/exports/${encodeURIComponent(name)}/run`);
export const getExportRuns = (name: string, limit = 20) =>
  api.get(`/api/exports/${encodeURIComponent(name)}/runs`, { params: { limit } });
